import { ThemeSelect } from '../uikit';
import Inbox from './Inbox';
import CreateWorkspaceDialog from './CreateWorkspaceDialog';
import WorkspacePeople from './WorkspacePeople';
import WorkspacePulse from './WorkspacePulse';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import WorkspaceLibrary from './WorkspaceLibrary';
import ArtifactViewer, { type ViewerTarget } from './ArtifactViewer';
import { X, Plus, MagnifyingGlass, DotsThree, Trash, PencilSimple, Sun, Moon, SignOut, ArrowRight, ArrowLeft, ArrowSquareOut, Stack, User, Users, CaretDown, Gear, Upload, ShieldCheck, CloudSlash , Eye, SquaresFour } from '@phosphor-icons/react';
import type { Deck, Phase, Folder } from '../types';
import type { Workspace } from '../api/client';
import { publishTemplate, forkDeck, getFolders, createFolder, updateFolder, deleteFolder, moveDeckToFolder } from '../api/client';
import { apiFetch } from '../api/auth-fetch';

const PHASE_COLORS: Record<string, string> = {
  gather: 'var(--ds-text-muted)',
  plan: 'var(--ds-text-muted)',
  generate: 'var(--ds-text-muted)',
  preview: 'var(--ds-text-muted)',
};

const DEMO_THUMBS: Record<string, { bg: string; text: string; accent: string; slideTitle: string }> = {
  'demo-ai-pitch': { bg: 'linear-gradient(135deg, #0a0a12 0%, #1a1a3e 60%, #2d1b69 100%)', text: '#e0e0ff', accent: '#a78bfa', slideTitle: 'NovaMind AI' },
  'demo-qbr': { bg: 'linear-gradient(135deg, #f8f8fa 0%, #e8eaef 100%)', text: '#111827', accent: '#2563eb', slideTitle: 'Q1 2026 Review' },
  'demo-product-launch': { bg: 'linear-gradient(135deg, #000 0%, #1a0a0a 50%, #0a0a0a 100%)', text: '#f5d590', accent: '#e8734a', slideTitle: 'AuraOS' },
  'demo-edu-workshop': { bg: 'linear-gradient(135deg, #faf8f5 0%, #fef3e2 100%)', text: '#1a1a2e', accent: '#0d9488', slideTitle: 'Data Storytelling' },
  'demo-problem-solution': { bg: 'linear-gradient(135deg, #fff 0%, #f0f4ff 100%)', text: '#111', accent: '#0033FF', slideTitle: 'FlowDesk' },
  'demo-sales-pitch': { bg: 'linear-gradient(135deg, #f8f7f4 0%, #fff 100%)', text: '#1e293b', accent: '#dc2626', slideTitle: 'ShieldNet' },
  'demo-company-overview': { bg: 'linear-gradient(135deg, #0c0c1d 0%, #1a1a2e 60%, #1e1a30 100%)', text: '#e4e4e7', accent: '#f59e0b', slideTitle: 'Lumina Labs' },
};

// ── LazySlide ─────────────────────────────────────────────────────────
//
// Thumbnail iframe that only mounts when scrolled into view + a 200px
// margin. Wrapped in React.memo + a custom equality check so the
// thumbnails persist across DeckDashboard re-renders.
//
// **Hoisted out of DeckDashboard intentionally.** If LazySlide is
// declared inside the parent function body, every parent render
// creates a brand-new component type — React treats it as a fresh
// component each time and remounts all the iframes, throwing away
// the already-loaded slide content and the IntersectionObserver
// state. Aligns PP main #120.
//
// The onClick callback takes the slide key as an argument (rather
// than being a closure over the key) so the caller can pass the
// same function reference for every row → memo equality holds.

type LazySlideProps = {
  src: string;
  idx: number;
  scale: number;
  slideW: number;
  slideH: number;
  onClickKey: string;
  onClick: (key: string) => void;
};

const LazySlide = React.memo(function LazySlide({
  src,
  idx,
  scale,
  slideW,
  slideH,
  onClickKey,
  onClick,
}: LazySlideProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={() => onClick(onClickKey)}
      style={{
        background: 'var(--bg-surface)', borderRadius: 3, aspectRatio: '16/9',
        position: 'relative', overflow: 'hidden', cursor: 'pointer',
        border: '1px solid var(--border)',
        transition: 'border-color 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {visible ? (
        <iframe
          src={src}
          title={`Slide ${idx + 1}`}
          loading="lazy"
          sandbox="allow-same-origin"
          style={{
            position: 'absolute', top: 0, left: 0,
            width: slideW, height: slideH,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 'none', pointerEvents: 'none',
          }}
        />
      ) : (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: 'var(--text-muted)',
            opacity: 0.3,
            animation: 'zenBreathe 3s ease-in-out infinite',
          }} />
        </div>
      )}
    </div>
  );
}, (prev, next) => (
  prev.src === next.src
  && prev.idx === next.idx
  && prev.scale === next.scale
  && prev.slideW === next.slideW
  && prev.slideH === next.slideH
  && prev.onClickKey === next.onClickKey
  // onClick intentionally not compared — caller should pass the same
  // function reference (typically a stable useCallback). If the
  // caller binds a fresh closure each render, that's their bug to
  // fix, not ours.
));

interface DeckDashboardProps {
  decks: Deck[];
  onSelectDeck: (id: string, prefetched?: Deck) => void;
  onCreateDeck: (opts?: { title?: string; narrative?: string; style?: string; templateRef?: string; folder_id?: string }) => Promise<Deck | void>;
  onDeleteDeck: (id: string) => void;
  onRenameDeck: (id: string, title: string) => void;
  workspaceId?: string | null;
  isCreating: boolean;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  onLogout: () => void;
  onOpenSettings?: (section?: 'personal'|'workspace') => void;
  onOpenAdmin?: () => void;
  isAdmin?: boolean;
  username?: string;
  userRole?: string;
  workspaces?: Workspace[];
  activeWorkspace?: string;
  onSelectWorkspace?: (wsId: string) => void;
  onCreateWorkspace?: (name: string) => Promise<void>;
  onRefreshDecks?: () => void;
}

interface DemoItem {
  id: string;
  title: string;
  description: string;
  narrative?: string;
  style?: string;
  slides?: number;
  author?: string;
  prompts?: string[];
  preview_url: string;
}


// undefined = not asked yet; null = asked, none available (e.g. 404).
let _demoDecksCache: DemoItem[] | null | undefined;

export default function DeckDashboard({
  decks, workspaceId, onSelectDeck, onCreateDeck, onDeleteDeck, onRenameDeck,
  isCreating, theme, onToggleTheme, onLogout, onOpenSettings, onOpenAdmin, isAdmin, username, userRole,
  workspaces, activeWorkspace, onSelectWorkspace, onCreateWorkspace, onRefreshDecks,
}: DeckDashboardProps) {
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [activityOpen,setActivityOpen]=useState(false);
  const [libraryViewer, setLibraryViewer] = useState<ViewerTarget | null>(null);
  const [visOverride, setVisOverride] = useState<Record<string, string>>({});
  const [freshDecks, setFreshDecks] = useState<Record<string, Deck>>({});
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [demos, setDemos] = useState<DemoItem[]>([]);
  const [activeDemo, setActiveDemo] = useState<DemoItem | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState<false | true | 'ws'>(false);
  const createWorkspaceTrigger=useRef<HTMLElement|null>(null);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [deckPage, setDeckPage] = useState(1);
  const [templatePage, setTemplatePage] = useState(1);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishFeedback, setPublishFeedback] = useState<{ deckId: string; ok: boolean; message: string } | null>(null);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const previewGridRef = useRef<HTMLDivElement>(null);

  const [previewHasContent, setPreviewHasContent] = useState<boolean | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ folder: Folder; inputValue: string } | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [moveSubmenu, setMoveSubmenu] = useState<string | null>(null);
  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => setAppVersion(d.version || '')).catch(() => {});
  }, []);
  const [panelRenaming, setPanelRenaming] = useState(false);
  const [panelRenameValue, setPanelRenameValue] = useState('');
  const [showMoveList, setShowMoveList] = useState(false);
  const [deleteDeckConfirm, setDeleteDeckConfirm] = useState<{ deck: Deck; inputValue: string } | null>(null);
  const [creatingInContext, setCreatingInContext] = useState<string | null>(null); // tracks which + New was clicked
  const [creatingFolderParent, setCreatingFolderParent] = useState<string | null>(null); // null=root, folder-id=subfolder
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const templateSentinelRef = useRef<HTMLDivElement>(null);


  // Responsive page sizes
  const getPageSize = () => {
    if (typeof window === 'undefined') return { decks: 20, templates: 6, cols: 3 };
    const w = window.innerWidth;
    if (w < 480) return { decks: 20, templates: 3, cols: 1 };
    if (w <= 1024) return { decks: 20, templates: 3, cols: 2 };
    return { decks: 20, templates: 3, cols: 3 };
  };
  const [pageSize, setPageSize] = useState(getPageSize);

  useEffect(() => {
    const onResize = () => setPageSize(getPageSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Session-cached: on instances without demo decks this endpoint
    // 404s — remember the answer instead of re-asking every mount.
    if (_demoDecksCache !== undefined) {
      if (_demoDecksCache) setDemos(_demoDecksCache);
      return;
    }
    fetch('/api/decks/demo-decks')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d && d.ok) {
          _demoDecksCache = d.data;
          setDemos(d.data);
        } else {
          _demoDecksCache = null;
        }
      })
      .catch(() => { _demoDecksCache = null; });
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if ((e.target as Element).closest?.('.v8ma-mobile-bar')) return;
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // Reset pagination when search or deck list changes
  useEffect(() => { setDeckPage(1); }, [searchQuery, decks.length]);


  // Clear creating context when done
  useEffect(() => {
    if (!isCreating) setCreatingInContext(null);
  }, [isCreating]);

  // Clear selection if selected deck is deleted
  useEffect(() => {
    if (selectedDeckId && !decks.find(d => d.id === selectedDeckId)) {
      setSelectedDeckId(null);
    }
  }, [decks, selectedDeckId]);

  // Current workspace — must match the page context
  const currentWorkspaceId = activeWorkspace
    || (workspaces && workspaces.length > 0 ? workspaces.find(w => w.type === 'personal')?.id || workspaces[0].id : undefined);

  // Fetch folders when workspace changes; reset UI state
  useEffect(() => {
    // Clear stale state from previous workspace
    setCreatingFolderParent(null);
    setNewFolderName('');
    setSelectedDeckId(null);
    setMenuOpen(null);
    setMoveSubmenu(null);
    setRenamingFolderId(null);
    setDeleteConfirm(null);

    setFolders([]);  // Clear immediately — prevents stale folder flash
    if (!currentWorkspaceId) return;
    getFolders(currentWorkspaceId).then(setFolders).catch(() => setFolders([]));
  }, [currentWorkspaceId]);

  const filteredDecks = searchQuery
    ? decks.filter(d => d.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : decks;

  const visibleDecks = filteredDecks.slice(0, deckPage * pageSize.decks);
  const visibleTemplates = demos.slice(0, templatePage * pageSize.templates);
  const hasMoreTemplates = demos.length > visibleTemplates.length;

  // Build folder tree from flat folders
  type FolderNode = Folder & { children: FolderNode[]; decks: Deck[] };
  const folderTree = useMemo(() => {
    const map = new Map<string, FolderNode>();
    const roots: FolderNode[] = [];

    folders.forEach(f => map.set(f.id, { ...f, children: [], decks: [] }));

    // Assign decks to folders
    filteredDecks.forEach(d => {
      if (d.folder_id && map.has(d.folder_id)) {
        map.get(d.folder_id)!.decks.push(d);
      }
    });

    // Build tree
    folders.forEach(f => {
      const node = map.get(f.id)!;
      if (f.parent_id && map.has(f.parent_id)) {
        map.get(f.parent_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort
    roots.sort((a, b) => a.sort_order - b.sort_order);

    return roots;
  }, [folders, filteredDecks]);

  // Unfiled decks (no folder)
  const unfiledDecks = useMemo(() => {
    const folderIds = new Set(folders.map(f => f.id));
    return filteredDecks.filter(d => !d.folder_id || !folderIds.has(d.folder_id));
  }, [filteredDecks, folders]);

  const visibleUnfiled = unfiledDecks.slice(0, deckPage * pageSize.decks);
  const hasMoreDecks = unfiledDecks.length > visibleUnfiled.length;

  const toggleFolder = (folderId: string) => {
    setCollapsedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  // Auto-load more templates when sentinel scrolls into view
  useEffect(() => {
    const el = templateSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setTemplatePage(p => p + 1);
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visibleTemplates.length, demos.length]);

  const handleFork = async (deck: Deck) => {
    setMenuOpen(null);
    try {
      const r = await forkDeck(deck.id);
      onSelectDeck(r.deck_id);
    } catch (err) {
      alert((err as Error).message || 'Fork failed');
    }
  };
  const handleRenameStart = (deck: Deck) => { setRenamingId(deck.id); setRenameValue(deck.title); setMenuOpen(null); };
  const handleRenameSubmit = (id: string) => { if (renameValue.trim()) onRenameDeck(id, renameValue.trim()); setRenamingId(null); };
  const handleDelete = (id: string) => { setMenuOpen(null); onDeleteDeck(id); };

  const handlePublishTemplate = async (deck: Deck) => {
    setMenuOpen(null);
    const slug = deck.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const templateId = prompt('Template ID (lowercase, hyphens only):', slug);
    if (!templateId) return;
    const sanitized = templateId.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (sanitized.length < 2) { alert('Template ID must be at least 2 characters.'); return; }

    setPublishingId(deck.id);
    setPublishFeedback(null);
    try {
      const result = await publishTemplate(deck.id, sanitized, deck.title);
      setPublishFeedback({ deckId: deck.id, ok: true, message: `Published as "${sanitized}" (${result.files.join(', ')})` });
    } catch (err: any) {
      setPublishFeedback({ deckId: deck.id, ok: false, message: err.message || 'Publish failed' });
    } finally {
      setPublishingId(null);
      setTimeout(() => setPublishFeedback(null), 5000);
    }
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };


  const enteringRef = useRef(false);
  const [entering, setEntering] = useState(false);
  const handleDeckClick = (deckId: string) => {
    if (renamingId === deckId) return;
    // Double-click: clicking already-selected deck enters it. Pass the
    // already-loaded Deck object as prefetched so selectDeck skips the
    // GET /api/decks/:id round-trip — we already have the row in the
    // dashboard's `decks` array. Cuts the click → activeDeck-set gap
    // from one network round-trip to zero, which is what was causing
    // the dashboard to flash for a beat after click.
    if (selectedDeckId === deckId) {
      enteringRef.current = true; setEntering(true);
      const prefetched = decks.find((d) => d.id === deckId);
      onSelectDeck(deckId, prefetched);
      return;
    }
    setSelectedDeckId(deckId);
    if(window.matchMedia("(max-width:700px)").matches) requestAnimationFrame(()=>document.querySelector<HTMLButtonElement>(".editorial-preview-close")?.focus());
    setPanelRenaming(false);
    setShowMoveList(false);
  };

  const selectedDeckBase = selectedDeckId ? decks.find(d => d.id === selectedDeckId) || null : null;
  const selectedDeck = selectedDeckId ? (freshDecks[selectedDeckId] ?? selectedDeckBase) : null;
  // Issue #5: the list payload goes stale while turns run elsewhere —
  // re-fetch the row whenever a deck is selected so phase/deliverable
  // state is live.
  useEffect(() => {
    if (!selectedDeckId) return;
    let cancelled = false;
    fetch(`/api/decks/${selectedDeckId}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const deck = d && (d.deck ?? d);
        if (!cancelled && deck && deck.id) setFreshDecks(prev => ({ ...prev, [deck.id]: deck as Deck }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDeckId]);

  // Render right panel content based on selected deck phase
  const renderRightPanelContent = () => {
    if (!selectedDeck) return null;

    const phase = selectedDeck.phase;

    const currentFolderName = selectedDeck.folder_id
      ? folders.find(f => f.id === selectedDeck.folder_id)?.name || null
      : null;

    return (
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: 32, overflowY: 'auto', animation: 'maFadeIn 0.4s ease both' }}>
        {/* Panel header — title */}
        <div style={{ marginBottom: 8 }}>
          {panelRenaming ? (
            <input
              autoFocus
              value={panelRenameValue}
              onChange={e => setPanelRenameValue(e.target.value)}
              onKeyDown={e => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter' && panelRenameValue.trim()) {
                  onRenameDeck(selectedDeck.id, panelRenameValue.trim());
                  setPanelRenaming(false);
                }
                if (e.key === 'Escape') setPanelRenaming(false);
              }}
              onBlur={() => {
                if (panelRenameValue.trim() && panelRenameValue.trim() !== selectedDeck.title) {
                  onRenameDeck(selectedDeck.id, panelRenameValue.trim());
                }
                setPanelRenaming(false);
              }}
              style={{
                fontSize: 'var(--fs-xl)', fontWeight: 400, color: 'var(--text-primary)', fontFamily: 'inherit',
                border: 'none', borderBottom: '1px solid var(--border-strong)', outline: 'none',
                background: 'none', padding: '4px 0', width: '100%',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--text-primary)'; }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 'var(--fs-xl)', fontWeight: 400, color: 'var(--text-primary)' }}>{selectedDeck.title}</span>
              <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{phase}</span>
            </div>
          )}
        </div>

        {/* Actions row — Rename · Move to... · Delete */}
        {!panelRenaming && (
          <details className="editorial-deck-actions"><summary>More options</summary><div>
            <button className="editorial-text-action"
              onClick={() => { setPanelRenaming(true); setPanelRenameValue(selectedDeck.title); }}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >Rename</button>
            <button className="editorial-text-action"
              onClick={() => setShowMoveList(!showMoveList)}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              {currentFolderName ? `Move from ${currentFolderName}` : 'Move to...'}
            </button>
            <button className="editorial-text-action"
              onClick={() => handleFork(selectedDeck)}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >Fork</button>
            <button className="editorial-text-action"
              onClick={async () => {
                const cur = visOverride[selectedDeck.id] ?? ((selectedDeck as unknown as { visibility?: string }).visibility ?? 'workspace');
                const next = cur === 'private' ? 'workspace' : 'private';
                const res = await fetch(`/api/decks/${selectedDeck.id}/visibility`, {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ visibility: next }),
                });
                if (res.ok) setVisOverride(prev => ({ ...prev, [selectedDeck.id]: next }));
                else alert('Only the deck owner can change visibility.');
              }}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              title="Private decks are visible only to explicit deck members"
            >{(visOverride[selectedDeck.id] ?? ((selectedDeck as unknown as { visibility?: string }).visibility ?? 'workspace')) === 'private' ? '🔒 Private' : '🌐 Workspace'}</button>
            <button className="editorial-text-action"
              onClick={() => setDeleteDeckConfirm({ deck: selectedDeck, inputValue: '' })}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >Delete</button>
          </div></details>
        )}

        {/* Move to — inline folder list */}
        {showMoveList && (
          <div style={{ marginBottom: 24, animation: 'maFadeIn 0.3s ease both' }}>
            <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 8 }}>
              Move to
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div
                onClick={() => {
                  moveDeckToFolder(selectedDeck.id, null).then(() => {
                    setShowMoveList(false);
                    onRefreshDecks?.();
                  });
                }}
                style={{
                  padding: '6px 0', fontSize: 'var(--fs-base)', fontWeight: 400, cursor: 'pointer',
                  color: !selectedDeck.folder_id ? 'var(--text-muted)' : 'var(--text-primary)',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => { if (selectedDeck.folder_id) e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = !selectedDeck.folder_id ? 'var(--text-muted)' : 'var(--text-primary)'; }}
              >
                Root
              </div>
              {folders.map(f => (
                <div
                  key={f.id}
                  onClick={() => {
                    moveDeckToFolder(selectedDeck.id, f.id).then(() => {
                      setShowMoveList(false);
                      onRefreshDecks?.();
                    });
                  }}
                  style={{
                    padding: '6px 0', fontSize: 'var(--fs-base)', fontWeight: 400, cursor: 'pointer',
                    color: selectedDeck.folder_id === f.id ? 'var(--text-muted)' : 'var(--text-primary)',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => { if (selectedDeck.folder_id !== f.id) e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = selectedDeck.folder_id === f.id ? 'var(--text-muted)' : 'var(--text-primary)'; }}
                >
                  {f.name}
                </div>
              ))}
            </div>
            <div style={{ height: 1, background: 'var(--border)', marginTop: 12 }} />
          </div>
        )}

        {/* Phase-specific content — hide when navigating into deck to prevent repaint flash */}
        {!entering && phase === 'preview' && renderPreviewGrid()}
        {!entering && (phase === 'plan' || phase === 'plan-ready') && renderPlanPhase()}
        {!entering && phase !== 'preview' && phase !== 'plan' && phase !== 'plan-ready' && renderDraftPhase()}
      </div>
    );
  };

  // Check if selected preview-phase deck has HTML artifacts, and get slide list
  const [previewSlideKeys, setPreviewSlideKeys] = useState<string[]>([]);
  const [previewCurrentVersion, setPreviewCurrentVersion] = useState<string | null>(null);
  const prevPreviewPhaseRef = useRef<string | null>(null);
  useEffect(() => {
    if (enteringRef.current) return; // Skip refresh when navigating into deck
    const deck = selectedDeckId ? (freshDecks[selectedDeckId] ?? decks.find(d => d.id === selectedDeckId)) : null;
    if (!deck || deck.phase !== 'preview') {
      setPreviewHasContent(null);
      setPreviewSlideKeys([]);
      prevPreviewPhaseRef.current = null;
      return;
    }
    // Avoid re-fetching if the deck phase hasn't changed for this selection
    const phaseKey = `${selectedDeckId}:${deck.phase}`;
    if (prevPreviewPhaseRef.current === phaseKey) return;
    prevPreviewPhaseRef.current = phaseKey;

    // Issue #5: the old /artifacts (content-hash) endpoint is gone in
    // this lineage — the fetch always failed and the pane fell back to
    // "Plan Phase" forever. artifact-groups is the live source.
    apiFetch(`/api/decks/${selectedDeckId}/artifact-groups`)
      .then(r => r.json())
      .then((d: { groups?: Array<{ version: string; legacy: boolean; files: Array<{ filename: string; slide_num: number | null }> }>; current_version?: string | null }) => {
        const groups = (d.groups ?? []).filter(g => !g.legacy);
        const current = d.current_version ?? null;
        setPreviewCurrentVersion(current);
        const ordered = current
          ? [...groups.filter(g => g.version === current), ...groups.filter(g => g.version !== current)]
          : groups;
        const keys: string[] = [];
        for (const g of ordered) {
          for (const f of g.files) {
            if (f.slide_num !== null && /\.html$/i.test(f.filename)) keys.push(`${g.version}/${f.filename}`);
          }
        }
        setPreviewHasContent(keys.length > 0);
        setPreviewSlideKeys(keys);
      })
      .catch(() => { setPreviewHasContent(false); setPreviewSlideKeys([]); });
  }, [selectedDeckId, freshDecks, decks]);

  // Measure container to compute thumbnail scale
  const [thumbCols, setThumbCols] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(0);
  const roRef = useRef<ResizeObserver | null>(null);

  // Callback ref — fires when grid div mounts/unmounts, guarantees element exists
  const previewGridCallback = useRef((el: HTMLDivElement | null) => {
    // Disconnect old observer
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    (previewGridRef as any).current = el;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth;
      if (w === 0) return; // not laid out yet
      const cols = w > 900 ? 3 : w > 500 ? 2 : 1;
      const gap = 10;
      setThumbCols(cols);
      setThumbWidth(Math.floor((w - gap * (cols - 1)) / cols));
    };
    measure();
    roRef.current = new ResizeObserver(measure);
    roRef.current.observe(el);
  }).current;

  // LazySlide is hoisted out of DeckDashboard (top of file) so its
  // component identity stays stable across parent renders — see the
  // comment above the declaration. Aligns PP main #120.
  //
  // Stable click handler so the LazySlide memo equality doesn't break
  // on every render of the preview grid.
  const handlePreviewSlideClick = useCallback((deckId: string) => {
    enteringRef.current = true;
    setEntering(true);
    // Pass the already-known Deck row so selectDeck doesn't refetch
    // it — same anti-flicker reason as handleDeckClick.
    const prefetched = decks.find((d) => d.id === deckId);
    onSelectDeck(deckId, prefetched);
  }, [onSelectDeck, decks]);

  const renderPreviewGrid = () => {
    if (!selectedDeckId) return null;
    if (previewHasContent === false) return renderPlanPhase();
    if (previewHasContent === null) return <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>Loading...</div>;

    const slides = previewSlideKeys.length > 0
      ? previewSlideKeys
      : Array.from({ length: 6 }, (_, i) => `__fallback_${i + 1}`);

    const SLIDE_W = 1280;
    const SLIDE_H = SLIDE_W * 9 / 16;
    const scale = thumbWidth > 0 ? thumbWidth / SLIDE_W : 0.25;

    return (
      <div ref={previewGridCallback} style={{
        display: 'grid',
        gridTemplateColumns: thumbCols > 0 ? `repeat(${thumbCols}, 1fr)` : 'repeat(3, 1fr)',
        gap: 10,
      }}>
        {slides.map((slideKey, idx) => {
          const shouldLoad = true; // browser manages concurrency (6 per domain)
          const src = slideKey.startsWith('__fallback_')
            ? `/api/decks/${selectedDeckId}/preview#slide-${String(idx + 1).padStart(2, '0')}`
            : `/api/decks/${selectedDeckId}/preview?artifact_id=${encodeURIComponent(`file:artifacts/${slideKey}`)}`;
          // Detect version folder change for section headers. Keys are
          // `${version}/${filename}` — the old 3-segment parse never
          // matched, so the headers silently vanished and multi-version
          // grids read as duplicated slides.
          const parts = slideKey.split('/');
          const folder = parts.length >= 2 ? parts[0] : '';
          const prevParts = idx > 0 ? slides[idx - 1].split('/') : [];
          const prevFolder = prevParts.length >= 2 ? prevParts[0] : '';
          const isNewVersion = idx === 0 || folder !== prevFolder;
          const folderLabel = folder && folder === previewCurrentVersion ? `${folder} · current` : folder;
          return (<React.Fragment key={slideKey}>
            {isNewVersion && folder && (
              <div style={{
                gridColumn: '1 / -1',
                fontSize: 'var(--fs-xs)', fontWeight: 500, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: idx === 0 ? '0 0 4px' : '12px 0 4px',
                borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
              }}>
                {folderLabel}
              </div>
            )}
            <LazySlide
              src={src}
              idx={idx}
              scale={scale}
              slideW={SLIDE_W}
              slideH={SLIDE_H}
              onClickKey={selectedDeckId}
              onClick={handlePreviewSlideClick}
            />
          </React.Fragment>);
        })}
      </div>
    );
  };

  const renderPlanPhase = () => {
    return (
      <div style={{ animation: 'maFadeIn 0.4s ease both' }}>
        <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 12 }}>
          Plan Phase
        </div>
        <div style={{ fontSize: 'var(--fs-base)', fontWeight: 400, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          Storyboard is being prepared. Enter to view details.
        </div>
      </div>
    );
  };

  const renderDraftPhase = () => {
    return (
      <div style={{ fontSize: 'var(--fs-base)', fontWeight: 400, color: 'var(--text-muted)', marginTop: 40 }}>
        Open this presentation to add your ideas and source material.
      </div>
    );
  };

  // ── Deck row helper ──
  const renderDeckRow = (deck: Deck) => {
    const isSelected = selectedDeckId === deck.id;
    return (
      <div key={deck.id} onClick={() => handleDeckClick(deck.id)}
        className="v8ma-deck-row" data-selected={isSelected}
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          padding: '8px 0 8px 8px',
          cursor: renamingId === deck.id ? 'default' : 'pointer',
          transition: 'color 0.2s', position: 'relative',
          borderLeft: isSelected ? '2px solid var(--text-primary)' : '2px solid transparent',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {renamingId === deck.id ? (
            <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
              onBlur={() => handleRenameSubmit(deck.id)} onClick={e => e.stopPropagation()}
              onKeyDown={e => { if (e.nativeEvent.isComposing) return; if (e.key === 'Enter') handleRenameSubmit(deck.id); if (e.key === 'Escape') setRenamingId(null); }}
              style={{
                width: '100%', fontSize: 'var(--fs-md)', fontWeight: 400, fontFamily: 'inherit',
                border: '1px solid var(--border-strong)', borderRadius: 4, padding: '4px 8px',
                background: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none',
              }} />
          ) : (
            <button className="deck-title-text" data-deck-id={deck.id} title={deck.title} onClick={e=>{e.stopPropagation();handleDeckClick(deck.id);}} style={{
              fontSize: 'var(--fs-lg)', fontWeight: 400, color: 'var(--text-primary)',
              display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              transition: 'color 0.2s',
            }}>{deck.title}</button>
          )}
        </div>

        {/* Meta (status + date) — hidden when selected */}
        {!isSelected && (
          <div className="deck-meta" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexShrink: 0, marginLeft: 16 }}>
            <span style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 400, letterSpacing: '0.08em',
              color: 'var(--text-muted)', textTransform: 'uppercase' as const,
            }}>{deck.phase}</span>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', minWidth: 60, textAlign: 'right' as const }}>{formatDate(deck.updated_at)}</span>
          </div>
        )}

        {/* Enter — shown when selected */}
        {isSelected && (
          <button
            onClick={e => { e.stopPropagation(); enteringRef.current = true; setEntering(true); onSelectDeck(deck.id); }}
            style={{
              fontFamily: 'inherit', fontSize: 'var(--fs-md)', fontWeight: 600,
              color: 'var(--text-primary)', background: 'none',
              border: 'none', padding: 0, cursor: 'pointer',
              marginLeft: 16, flexShrink: 0, whiteSpace: 'nowrap',
              transformOrigin: 'right center',
              transition: 'color 0.2s, transform 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
          >
            Open &rarr;
          </button>
        )}
      </div>
    );
  };

  // ── Folder menu helper ──
  const renderFolderMenu = (folder: FolderNode) => (
    <div onClick={e => e.stopPropagation()} style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 10,
      background: 'var(--bg-primary)', border: '1px solid var(--border)',
      borderRadius: 4, boxShadow: 'none',
      minWidth: 140, overflow: 'hidden',
    }}>
      <button onClick={() => {
        setRenamingFolderId(folder.id);
        setRenameFolderValue(folder.name);
        setMenuOpen(null);
      }} style={{
        width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 'var(--fs-xs)', color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left',
        transition: 'background 0.3s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      >
        <PencilSimple size={12} weight="light" /> Rename
      </button>
      <button onClick={() => {
        setMenuOpen(null);
        setCreatingFolderParent(folder.id);
        setNewFolderName('');
        setTimeout(() => newFolderRef.current?.focus(), 50);
      }} style={{
        width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 'var(--fs-xs)', color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left',
        transition: 'background 0.3s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      >
        <Plus size={12} weight="light" /> New subfolder
      </button>
      <button onClick={() => {
        setMenuOpen(null);
        setDeleteConfirm({ folder, inputValue: '' });
      }} style={{
        width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontFamily: 'inherit', textAlign: 'left',
        transition: 'background 0.3s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      >
        <Trash size={12} weight="light" /> Delete
      </button>
    </div>
  );

  // ── Recursive folder node renderer ──
  const renderFolderNode = (folder: FolderNode, depth: number, parentPath: string[] = []): React.ReactNode => {
    const currentPath = [...parentPath, folder.name];
    return (
    <div key={folder.id} style={{ marginBottom: depth === 0 ? 28 : 12, paddingLeft: depth * 16 }}>
      {/* Folder header */}
      <div className="v8ma-folder-row" onClick={() => toggleFolder(folder.id)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        cursor: 'pointer', userSelect: 'none', marginBottom: 12,
        position: 'relative',
      }}>
        <span style={{
          fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', transition: 'transform 0.2s',
          display: 'inline-block', width: 8,
          transform: collapsedFolders.has(folder.id) ? 'rotate(0deg)' : 'rotate(90deg)',
        }}>&#9658;</span>
        {renamingFolderId === folder.id ? (
          <input
            autoFocus
            value={renameFolderValue}
            onChange={e => setRenameFolderValue(e.target.value)}
            onClick={e => e.stopPropagation()}
            onBlur={() => {
              if (renameFolderValue.trim() && renameFolderValue.trim() !== folder.name) {
                updateFolder(folder.id, { name: renameFolderValue.trim() }).then(updated => {
                  setFolders(prev => prev.map(f => f.id === folder.id ? { ...f, name: updated.name } : f));
                }).catch(() => {});
              }
              setRenamingFolderId(null);
            }}
            onKeyDown={e => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
              }
              if (e.key === 'Escape') { setRenamingFolderId(null); }
            }}
            style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.08em',
              border: '1px solid var(--border-strong)', borderRadius: 3, padding: '2px 6px',
              background: 'var(--bg-primary)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit',
            }}
          />
        ) : (
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 0 }}>
            {depth > 0 && parentPath.length > 0 && (
              <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {parentPath.join(' / ')} <span style={{ margin: '0 2px', color: 'var(--text-muted)' }}>/</span>
              </span>
            )}
            <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
              {folder.name}
            </span>
          </span>
        )}
        <span style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 4 }} />
        {/* Hover-reveal folder menu */}
        <button className="folder-menu-btn" onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === `folder-${folder.id}` ? null : `folder-${folder.id}`); }} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', opacity: 0, transition: 'opacity 0.3s, color 0.2s',
          display: 'flex', alignItems: 'center',
        }}>
          <DotsThree size={14} weight="light" />
        </button>
        {/* Folder context menu */}
        {menuOpen === `folder-${folder.id}` && renderFolderMenu(folder)}
      </div>

      {/* Folder items — collapsible */}
      {!collapsedFolders.has(folder.id) && (
        <div style={{ overflow: 'hidden' }}>
          {/* Actions at TOP */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '6px 0', marginBottom: 4 }}>
            <span
              onClick={e => { e.stopPropagation(); if (!isCreating) onCreateDeck({ folder_id: folder.id }).then(deck => { if (deck) onSelectDeck(deck.id, deck); }); }}
              style={{
                fontSize: 'var(--fs-sm)', fontWeight: 400,
                color: 'var(--text-muted)', cursor: isCreating ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s',
                opacity: isCreating ? 0.5 : 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              + New
            </span>
            <span
              onClick={e => { e.stopPropagation(); setCreatingFolderParent(folder.id); setNewFolderName(''); setTimeout(() => newFolderRef.current?.focus(), 50); }}
              style={{
                fontSize: 'var(--fs-sm)', fontWeight: 400,
                color: 'var(--text-muted)', cursor: 'pointer',
                transition: 'color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              + Folder
            </span>
          </div>
          {/* Inline subfolder creation */}
          {creatingFolderParent === folder.id && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 8 }}>
              <input
                ref={newFolderRef}
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => {
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter' && newFolderName.trim() && currentWorkspaceId) {
                    createFolder(currentWorkspaceId, newFolderName.trim(), folder.id).then(f => {
                      setFolders(prev => [...prev, f]);
                      setCreatingFolderParent(null);
                      setNewFolderName('');
                    }).catch(() => {});
                  }
                  if (e.key === 'Escape') { setCreatingFolderParent(null); setNewFolderName(''); }
                }}
                onBlur={() => { if (!newFolderName.trim()) { setCreatingFolderParent(null); setNewFolderName(''); } }}
                placeholder="Subfolder name..."
                style={{
                  fontFamily: 'inherit', fontSize: 'var(--fs-base)', fontWeight: 400,
                  color: 'var(--text-primary)', background: 'none',
                  border: 'none', borderBottom: '1px solid var(--border-strong)',
                  outline: 'none', padding: '4px 0', width: 160,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--text-primary)'; }}
              />
              <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>Enter ↵</span>
            </div>
          )}
          {/* Decks */}
          {folder.decks.map(deck => renderDeckRow(deck))}
          {/* Child folders */}
          {folder.children.map(child => renderFolderNode(child, depth + 1, currentPath))}
        </div>
      )}
    </div>
    );
  };

  // ── Demo Detail View ──
  if (activeDemo) {
    const thumb = DEMO_THUMBS[activeDemo.id] || { bg: 'var(--ds-surface)', text: 'var(--ds-text)', accent: 'var(--ds-text-muted)', slideTitle: activeDemo.title };
    const ctaText = activeDemo.prompts && activeDemo.prompts.length > 0
      ? activeDemo.prompts[0]
      : 'Use this template';
    return (
      <div style={{ height: 'var(--app-height)', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
        <header style={{
          height: 44, borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center',
          padding: '0 20px', flexShrink: 0, background: 'var(--bg-primary)', gap: 12,
        }}>
          <button
            onClick={() => setActiveDemo(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              background: 'none', border: 'none', borderRadius: 6,
              cursor: 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit',
              fontSize: 'var(--fs-sm)', fontWeight: 500, transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            <ArrowLeft size={14} weight="light" />
            Back
          </button>
          <div style={{ width: 1, height: 16, background: 'var(--border)', opacity: 0.6 }} />
          <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-primary)' }}>
            {activeDemo.title}
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => window.open(activeDemo.preview_url, '_blank')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              background: 'none', border: 'none', borderRadius: 6,
              cursor: 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit',
              fontSize: 'var(--fs-sm)', fontWeight: 500, transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            <ArrowSquareOut size={13} weight="light" />
            Fullscreen
          </button>
        </header>

        <div className="demo-detail" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <div className="demo-detail-iframe" style={{ flex: 1, minWidth: 0, background: 'var(--ds-canvas)', position: 'relative' }}>
            <iframe
              src={activeDemo.preview_url}
              title={activeDemo.title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            />
          </div>

          <div className="demo-detail-sidebar" style={{
            width: 320, flexShrink: 0, borderLeft: '1px solid var(--border)',
            background: 'var(--bg-surface)', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', padding: '24px 20px', gap: 24,
          }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {activeDemo.narrative && (
                <span style={{
                  fontSize: 'var(--fs-2xs)', padding: '3px 10px', borderRadius: 4,
                  background: `${thumb.accent}18`, color: thumb.accent, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>{activeDemo.narrative}</span>
              )}
              {activeDemo.slides && (
                <span style={{
                  fontSize: 'var(--fs-2xs)', padding: '3px 10px', borderRadius: 4,
                  background: 'var(--bg-primary)', color: 'var(--text-muted)', fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <Stack size={10} weight="light" />
                  {activeDemo.slides} slides
                </span>
              )}
            </div>

            <p style={{ fontSize: 'var(--fs-base)', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
              {activeDemo.description}
            </p>

            <button
              onClick={() => onCreateDeck({ narrative: activeDemo.narrative, style: activeDemo.style, templateRef: activeDemo.id })}
              disabled={isCreating}
              style={{
                width: '100%', padding: '10px 16px',
                fontSize: 'var(--fs-sm)', fontWeight: 400,
                background: 'var(--text-primary)', color: 'var(--bg-primary)',
                border: 'none', borderRadius: 4,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: isCreating ? 0.6 : 1, transition: 'opacity 0.3s',
                lineHeight: 1.3, textAlign: 'center',
              }}
            >
              {isCreating ? 'Creating...' : ctaText}
              {!isCreating && <ArrowRight size={15} weight="light" style={{ flexShrink: 0 }} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Dashboard View ──
  return (
    <div className="v8ma-ws-page coastal-dashboard" style={{ height: 'var(--app-height)', position: 'relative', background: 'var(--bg-primary)' }}>
      {/* CSS for hover reveals + responsive grid */}
      <style>{`
        .v8ma-side-strip:hover .v8ma-ws-name { opacity: 1 !important; }
        .v8ma-deck-row:hover .deck-menu-btn { opacity: 1 !important; }
        .v8ma-deck-row:hover .deck-title-text { color: var(--text-primary) !important; }
        .v8ma-folder-row:hover .folder-menu-btn { opacity: 1 !important; }

        .v8ma-main-content::-webkit-scrollbar { width: 2px; }
        .v8ma-main-content::-webkit-scrollbar-track { background: transparent; }
        .v8ma-main-content::-webkit-scrollbar-thumb { background: var(--ds-shadow-color); border-radius: 1px; }

        .v8ma-right-panel-scroll::-webkit-scrollbar { width: 2px; }
        .v8ma-right-panel-scroll::-webkit-scrollbar-track { background: transparent; }
        .v8ma-right-panel-scroll::-webkit-scrollbar-thumb { background: var(--ds-shadow-color); border-radius: 1px; }

        @media (min-width: 1400px) {
          .v8ma-slides-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
        @media (max-width: 1100px) {
          .v8ma-slides-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 800px) {
          .v8ma-slides-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
        }

        @media (max-width: 700px) {
          /* Mobile: hide desktop side strip, show mobile bottom bar */
          .v8ma-side-strip { display: none !important; }
          .v8ma-mobile-bar { display: flex !important; }

          /* Dashboard layout: column-reverse (preview top, list bottom) */
          .v8ma-layout {
            flex-direction: column-reverse !important;
          }
          .v8ma-left-panel {
            width: 100% !important; max-width: none !important;
            height: auto !important; flex: 1 !important; min-height: 0 !important;
            border-right: none !important;
          }
          .v8ma-right-panel {
            display: flex !important; flex: none !important;
            width: 100% !important; aspect-ratio: 4/3;
            max-height: 50vh;
          }
          .v8ma-main-content {
            position: relative !important;
            left: 0 !important; right: 0 !important;
            bottom: auto !important; top: auto !important;
            max-height: none !important;
            padding: 12px 16px !important;
          }
          .v8ma-nav { left: 16px !important; top: 8px !important; }
        }

        @media (max-width: 479px) {
          .v8ma-main-content {
            left: 0 !important;
            bottom: 48px !important;
            right: 0 !important;
          }
          .v8ma-nav { left: 16px !important; }

          .demo-detail { flex-direction: column !important; }
          .demo-detail-iframe {
            flex: none !important;
            width: 100% !important;
            aspect-ratio: 16/9;
            position: relative !important;
          }
          .demo-detail-sidebar {
            width: 100% !important;
            border-left: none !important;
            border-top: 1px solid var(--border) !important;
            flex-shrink: 1 !important;
            overflow-y: auto !important;
            padding: 20px 16px !important;
          }
        }

        @media (min-width: 480px) and (max-width: 1024px) {
          .demo-detail-sidebar { width: 280px !important; }
        }

        @media (max-width: 900px) {
          .v8ma-slides-grid .slide-card-item { aspect-ratio: 16 / 10 !important; }
        }
      `}</style>

      {showNewTeam&&onCreateWorkspace&&<CreateWorkspaceDialog returnFocusRef={createWorkspaceTrigger} onCreate={onCreateWorkspace} onClose={()=>setShowNewTeam(false)}/>}
      {/* Floating nav — top-left (brand only) */}
      <div className="v8ma-nav" style={{ position: 'fixed', top: 20, left: 60, zIndex: 20 }}>
        <span className="coastal-brand"><span className="coastal-logo" aria-hidden="true"><i/><i/><i/></span>PitchPilot</span>
      </div>

      <div className="pp-dashboard-inbox pp-dashboard-inbox-mobile"><Inbox /></div>
      {workspaceId&&<div className="coastal-people-bar" style={{position:'fixed',top:14,right:24,zIndex:25}}><WorkspacePeople key={workspaceId} workspaceId={workspaceId} workspaceName={workspaces?.find(w=>w.id===workspaceId)?.name} onOpenSettings={onOpenSettings?()=>onOpenSettings('workspace'):undefined}/></div>}
      {/* Search moved into left panel header */}

      {/* Side Strip — workspace switcher + bottom controls */}
      <div
        className="v8ma-side-strip"
        style={{
          position: 'fixed', top: 0, left: 0,
          width: 40, height: 'var(--app-height)', zIndex: 15,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden', cursor: 'pointer',
          transition: 'width 0.3s ease, background 0.3s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.width = '160px'; e.currentTarget.style.background = 'var(--bg-surface)'; }}
        onMouseLeave={e => { e.currentTarget.style.width = '40px'; e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ position: 'absolute', top: 0, right: 0, width: 1, height: '100%', background: 'var(--ds-surface-subtle)' }} />

        {/* Workspace items — centered */}
        <div className="coastal-workspaces" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, width: '100%' }}>
          <span className="coastal-nav-label">Workspaces</span>
          {workspaces && workspaces.length > 0 && workspaces.map(ws => {
            const isActive = ws.id === activeWorkspace;
            const initial = (ws.name || '?')[0].toUpperCase();
            return (
              <div
                key={ws.id}
                className="coastal-workspace-link" data-active={isActive}
                role="button" tabIndex={0} aria-label={ws.name} aria-current={isActive?'page':undefined}
                onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onSelectWorkspace?.(ws.id);}}}
                onClick={() => onSelectWorkspace?.(ws.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '6px 12px',
                  position: 'relative', whiteSpace: 'nowrap',
                  borderRadius: 3, transition: 'background 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                  width: 2, height: 2, borderRadius: '50%',
                  background: isActive ? 'var(--text-primary)' : 'transparent',
                }} />
                <span style={{
                  fontSize: 'var(--fs-xs)', color: isActive ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 400,
                  width: 16, textAlign: 'center', flexShrink: 0,
                }}>{initial}</span>
                <span className="v8ma-ws-name" style={{
                  fontSize: 'var(--fs-xs)', color: isActive ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 400,
                  opacity: 0, transition: 'opacity 0.3s',
                }}>{ws.name}</span>
              </div>
            );
          })}
          {onCreateWorkspace&&<button aria-label="New workspace" title="New workspace" onClick={e=>{createWorkspaceTrigger.current=e.currentTarget;setShowNewTeam(true);}} style={{display:'grid',placeItems:'center',width:28,height:28,padding:0,border:0,borderRadius:6,background:'transparent',color:'var(--text-muted)',fontSize:24,fontWeight:300,cursor:'pointer'}}>+</button>}
        </div>

        {/* Bottom controls — theme + user menu */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          padding: '16px 0', width: '100%', flexShrink: 0,
        }}>
          <div className="pp-sidebar-inbox"><Inbox /></div>
          <ThemeSelect/>
          {/* UI scale control — single button, cycles through scales */}
          <div
            onClick={() => {
              const scales = ['1', '1.1', '1.2'];
              const current = localStorage.getItem('pp_ui_scale') || '1';
              const idx = scales.indexOf(current);
              const next = scales[(idx + 1) % scales.length];
              document.documentElement.style.setProperty('--fs-scale', next);
              localStorage.setItem('pp_ui_scale', next);
            }}
            title="Adjust UI scale"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '6px 12px',
              whiteSpace: 'nowrap', borderRadius: 3,
              transition: 'background 0.2s', cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ width: 16, textAlign: 'center', flexShrink: 0, fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
              Aa
            </span>
            <span className="v8ma-ws-name" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400, opacity: 0, transition: 'opacity 0.3s' }}>
              Scale
            </span>
          </div>

          {/* User menu */}
          <div ref={userMenuRef} style={{ width: '100%', position: 'relative' }}>
            <div
              onClick={e => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '6px 12px',
                whiteSpace: 'nowrap', borderRadius: 3,
                transition: 'background 0.2s', cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--ds-surface-subtle)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: '50%', background: 'var(--bg-surface)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0,
              }}>
                {(username || '?')[0].toUpperCase()}
              </span>
              <span className="v8ma-ws-name" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 400, opacity: 0, transition: 'opacity 0.3s' }}>
                {username || 'Account'}
              </span>
            </div>
            {userMenuOpen && (() => {
              const menuItemStyle: React.CSSProperties = {
                width: '100%', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 'var(--fs-xs)', color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left',
                transition: 'background 0.15s', letterSpacing: '0.01em',
              };
              const iconStyle: React.CSSProperties = { color: 'var(--text-muted)', flexShrink: 0 };
              const onHover = (e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; };
              const onLeave = (e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = 'none'; };
              return (
              <div className="coastal-account-menu" style={{
                position: 'fixed', bottom: 16, left: 48,
                width: 220, background: 'var(--bg-primary)', border: '1px solid var(--border)',
                borderRadius: 10, boxShadow: '0 8px 32px var(--ds-shadow-color)', overflow: 'hidden', zIndex: 200,
              }}>
                {/* User identity */}
                {username && (
                  <div style={{ padding: '14px 16px 10px', fontSize: 'var(--fs-sm)', color: 'var(--text-primary)', fontWeight: 500 }}>
                    {username}
                  </div>
                )}
                <div style={{ height: 1, background: 'var(--border)', margin: '0 12px' }} />

                {/* Actions */}
                <div style={{ padding: '4px 0' }}>
                  {onOpenSettings && <>
                    <button onClick={()=>{setUserMenuOpen(false);onOpenSettings('personal');}} style={menuItemStyle} onMouseEnter={onHover} onMouseLeave={onLeave}><Gear size={14} weight="light" style={iconStyle}/>Personal settings</button>
                  </>}
                  {isAdmin && onOpenAdmin && (
                    <button onClick={() => { setUserMenuOpen(false); onOpenAdmin(); }} style={menuItemStyle} onMouseEnter={onHover} onMouseLeave={onLeave}>
                      <ShieldCheck size={14} weight="light" style={iconStyle} />
                      Admin
                    </button>
                  )}
                </div>

                <div style={{ height: 1, background: 'var(--border)', margin: '0 12px' }} />

                {/* Tools */}
                <div style={{ padding: '4px 0' }}>
                  <a href="https://petrus-han.github.io/PitchRasp/" target="_blank" rel="noopener noreferrer"
                    style={{ ...menuItemStyle, textDecoration: 'none', color: 'var(--text-primary)' }}
                    onMouseEnter={onHover} onMouseLeave={onLeave}
                  >
                    <CloudSlash size={14} weight="light" style={iconStyle} />
                    <span style={{ flex: 1 }}>Offline Editor</span>
                    <ArrowSquareOut size={11} weight="light" style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                  </a>
                </div>

                <div style={{ height: 1, background: 'var(--border)', margin: '0 12px' }} />

                {/* Quick guide (issue #6: the tour's final step promises this) */}
                <div style={{ padding: '4px 0' }}>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      try { localStorage.removeItem('pp_onboarding_seen_v1'); } catch {}
                      window.dispatchEvent(new Event('pp:onboarding-replay'));
                    }}
                    style={{ ...menuItemStyle, color: 'var(--text-primary)' }} onMouseEnter={onHover} onMouseLeave={onLeave}
                  >
                    <Eye size={14} weight="light" style={iconStyle} />
                    Quick guide
                  </button>
                </div>

                <div style={{ height: 1, background: 'var(--border)', margin: '0 12px' }} />

                {/* Footer */}
                <div style={{ padding: '4px 0' }}>
              <button onClick={() => { setUserMenuOpen(false); onLogout(); }} style={{ ...menuItemStyle, color: 'var(--text-muted)' }} onMouseEnter={onHover} onMouseLeave={onLeave}>
                    <SignOut size={14} weight="light" />
                    Sign out
                  </button>
                </div>

                {/* Version */}
                <div style={{ padding: '6px 16px 10px', fontSize: '10px', color: 'var(--text-muted)', opacity: 0.4, letterSpacing: '0.03em' }}>
                  PitchPilot v{appVersion}
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Layout — left-right split */}
      {/* Mobile bottom bar — hidden on desktop, shown on mobile via CSS */}
      <div className="v8ma-mobile-bar" style={{
        display: 'none', /* shown by @media max-width:700px */
        position: 'fixed', bottom: 0, left: 0, right: 0, height: 44, zIndex: 20,
        background: 'var(--bg-surface)', borderTop: '1px solid var(--border)',
        alignItems: 'center', justifyContent: 'space-evenly', padding: '0 8px',
      }}>
        {/* Workspace selector */}
        <div style={{ position: 'relative' }}>
          <button aria-label="Switch workspace" onClick={() => setUserMenuOpen(prev => prev === 'ws' ? false : 'ws')}
            style={{ background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
              {(workspaces?.find(w => w.id === activeWorkspace)?.name || 'Personal')?.slice(0, 8)}
            </span>
          </button>
          {userMenuOpen === 'ws' && (
            <div style={{ position: 'absolute', bottom: 44, left: 0, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', zIndex: 200, minWidth: 140 }}>
              {workspaces?.map(ws => (
                <button key={ws.id} onClick={() => { onSelectWorkspace?.(ws.id); setUserMenuOpen(false); }}
                  style={{ width: '100%', padding: '10px 14px', background: ws.id === activeWorkspace ? 'var(--bg-surface)' : 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--text-primary)', fontFamily: 'inherit', textAlign: 'left' }}>
                  {ws.name}
                </button>
              ))}
              {onCreateWorkspace&&<button aria-label="New workspace" title="New workspace" onClick={e=>{createWorkspaceTrigger.current=e.currentTarget.closest('.v8ma-mobile-bar')?.querySelector<HTMLElement>('[aria-label="Switch workspace"]')||null;setUserMenuOpen(false);setShowNewTeam(true);}} style={{width:'100%',padding:'8px 14px',background:'none',border:0,color:'var(--text-primary)',fontSize:24,cursor:'pointer'}}>+</button>}
            </div>
          )}
        </div>
        {/* Font scale */}
        <button aria-label="Adjust text size" onClick={() => {
          const scales = ['1', '1.1', '1.2'];
          const current = localStorage.getItem('pp_ui_scale') || '1';
          const idx = scales.indexOf(current);
          const next = scales[(idx + 1) % scales.length];
          document.documentElement.style.setProperty('--fs-scale', next);
          localStorage.setItem('pp_ui_scale', next);
        }} style={{ background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          Aa
        </button>
        <ThemeSelect/>
        {/* User menu */}
        <div style={{ position: 'relative' }}>
          <button aria-label="Account menu" onClick={() => setUserMenuOpen(prev => prev === true ? false : true)}
            style={{ background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: 'var(--text-muted)' }}>
              {(username || '?')[0].toUpperCase()}
            </span>
          </button>
          {userMenuOpen === true && (
            <div style={{ position: 'absolute', bottom: 44, right: 0, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', zIndex: 200, minWidth: 160 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{username}</div>
              {onOpenSettings&&<button onClick={()=>{setUserMenuOpen(false);onOpenSettings('personal');}} style={{width:'100%',padding:'10px 14px',background:'none',border:0,color:'var(--text-primary)',textAlign:'left'}}>Personal settings</button>}
              <button onClick={()=>{setUserMenuOpen(false);window.dispatchEvent(new Event('pp:onboarding-replay'));}} style={{width:'100%',padding:'10px 14px',background:'none',border:0,color:'var(--text-primary)',textAlign:'left'}}>Quick guide</button>
              <button onClick={() => { setUserMenuOpen(false); onLogout(); }} style={{ width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--ds-danger)', fontFamily: 'inherit', textAlign: 'left' }}>Logout</button>
            </div>
          )}
        </div>
      </div>

      <div className={`v8ma-layout${selectedDeck ? " has-preview" : ""}`} style={{ display: 'flex', height: 'var(--app-height)', width: '100%' }}>
        {/* Left Panel — deck list anchored bottom-left */}
        <div className="v8ma-left-panel" style={{ width: '38.2%', maxWidth: 480, minWidth: 280, height: 'var(--app-height)', position: 'relative', flexShrink: 0 }}>
          <div className="v8ma-main-content" style={{
            position: 'absolute',
            bottom: 80, left: 60, right: 20,
            maxHeight: 'calc(var(--app-height) - 160px)', overflowY: 'auto',
            animation: 'maFadeIn 0.5s ease both',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 'calc(24px * var(--fs-scale))', fontWeight: 400, color: 'var(--text-primary)', margin: 0 }}>
                Presentations
              </h2>
              <div className="v8ma-search-area">
                {searchOpen ? (
                  <div style={{ position: 'relative' }}>
                    <MagnifyingGlass size={12} weight="light" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      ref={searchInputRef}
                      type="text" placeholder="Search..." value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onBlur={() => { if (!searchQuery.trim()) { setSearchOpen(false); setSearchQuery(''); } }}
                      onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                      style={{
                        width: 160, padding: '6px 10px 6px 30px', fontSize: 'var(--fs-xs)',
                        border: 'none', borderBottom: '1px solid var(--border)', borderRadius: 0,
                        background: 'transparent', color: 'var(--text-primary)',
                        fontFamily: 'inherit', outline: 'none', fontWeight: 400,
                      }}
                    />
                  </div>
                ) : (
                  <button
                    aria-label="Search presentations" onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                    style={{
                      background: 'none', border: 'none',
                      padding: '4px 0', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', fontWeight: 400,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  >
                    <MagnifyingGlass size={14} weight="light" />
                  </button>
                )}
              </div>
            </div>

            {/* Top actions — + New / + Folder */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <button className="coastal-list-create" aria-disabled={isCreating}
                  onClick={() => { if (!isCreating) onCreateDeck().then(deck => { if (deck) onSelectDeck(deck.id, deck); }); }}
                  style={{
                    fontSize: 'var(--fs-md)', fontWeight: 600,
                    color: 'var(--text-primary)', cursor: isCreating ? 'not-allowed' : 'pointer',
                    transition: 'color 0.2s, transform 0.2s ease',
                    transformOrigin: 'left center',
                    display: 'inline-block',
                    opacity: isCreating ? 0.5 : 1,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {isCreating ? "Creating…" : "+ New presentation"}
                </button>
                <button className="editorial-text-action"
                  onClick={() => { setCreatingFolderParent('__root__'); setNewFolderName(''); setTimeout(() => newFolderRef.current?.focus(), 50); }}
                  style={{
                    fontSize: 'var(--fs-sm)', fontWeight: 400,
                    color: 'var(--text-secondary)', cursor: 'pointer',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                >
                  + Folder
                </button>
                {workspaceId && (
                  <button className="editorial-text-action"
                    onClick={() => setLibraryOpen(true)}
                    title="Browse all built pages in this workspace"
                    style={{
                      fontSize: 'var(--fs-sm)', fontWeight: 400,
                      color: 'var(--text-secondary)', cursor: 'pointer',
                      transition: 'color 0.2s', marginLeft: 'auto',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  >
                    <SquaresFour size={16} weight="regular" /> Library
                  </button>
                )}
              </div>

            </div>

            {/* Unfiled decks — before folders */}
            {unfiledDecks.length > 0 && (
              <div className="deck-grid" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {unfiledDecks.slice(0, deckPage * pageSize.decks).map(deck => renderDeckRow(deck))}
              </div>
            )}

            {/* Folder sections */}
            {folderTree.length > 0 && (
              <div>
                {folderTree.map(folder => renderFolderNode(folder, 0))}
              </div>
            )}

            {/* Empty state — no separate button, bottom actions cover it */}

            {hasMoreDecks && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={() => setDeckPage(p => p + 1)}
                  style={{
                    padding: '8px 24px', fontSize: 'var(--fs-xs)', fontWeight: 500,
                    background: 'none', border: '1px solid var(--border)', borderRadius: 8,
                    cursor: 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'none'; }}
                >
                  Show more ({unfiledDecks.length - visibleUnfiled.length} remaining)
                </button>
              </div>
            )}

            {searchQuery && filteredDecks.length === 0 && decks.length > 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                No presentations found for &ldquo;{searchQuery}&rdquo;
              </div>
            )}


            {decks.length === 0 && !searchQuery && <p className="editorial-empty">Your next presentation starts here. Add an idea or source material after creating one.</p>}

            {/* Inline folder name input */}
            {creatingFolderParent === '__root__' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <input
                  ref={newFolderRef}
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => {
                    if (e.nativeEvent.isComposing) return;
                    if (e.key === 'Enter' && newFolderName.trim() && currentWorkspaceId) {
                      createFolder(currentWorkspaceId, newFolderName.trim()).then(folder => {
                        setFolders(prev => [...prev, folder]);
                        setCreatingFolderParent(null);
                        setNewFolderName('');
                      }).catch(() => {});
                    }
                    if (e.key === 'Escape') { setCreatingFolderParent(null); setNewFolderName(''); }
                  }}
                  onBlur={() => { if (!newFolderName.trim()) { setCreatingFolderParent(null); setNewFolderName(''); } }}
                  placeholder="Folder name..."
                  style={{
                    fontFamily: 'inherit', fontSize: 'var(--fs-base)', fontWeight: 400,
                    color: 'var(--text-primary)', background: 'none',
                    border: 'none', borderBottom: '1px solid var(--border-strong)',
                    outline: 'none', padding: '4px 0', width: 180,
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--text-primary)'; }}
                />
                <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>Enter ↵</span>
              </div>
            )}

            {workspaceId && <details className="editorial-activity" onToggle={e=>setActivityOpen(e.currentTarget.open)}><summary>Workspace activity</summary>{activityOpen&&<WorkspacePulse key={workspaceId} workspaceId={workspaceId} onSelectDeck={onSelectDeck}/>}</details>}

            {/* ── Templates Section — hidden for v0.2.0 ── */}
            {false && demos.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <div style={{ height: 1, background: 'var(--border)', marginBottom: 24 }} />
                <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px 0' }}>
                  Templates
                </h2>
                <div className="template-grid" style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12,
                }}>
                  {visibleTemplates.map((demo) => {
                    const thumb = DEMO_THUMBS[demo.id] || { bg: 'var(--ds-surface-subtle)', text: 'var(--ds-text)', accent: 'var(--ds-accent)', slideTitle: demo.title };
                    return (
                      <div
                        key={demo.id}
                        className="template-card"
                        onClick={() => setActiveDemo(demo)}
                        style={{
                          background: 'var(--bg-surface)', border: '1px solid var(--border)',
                          borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                          transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                        }}
                        onMouseEnter={e => { const el = e.currentTarget; el.style.borderColor = `color-mix(in srgb, ${thumb.accent} 25%, transparent)`; el.style.boxShadow = `0 4px 20px color-mix(in srgb, ${thumb.accent} 8%, transparent)`; el.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={e => { const el = e.currentTarget; el.style.borderColor = 'var(--ds-border)'; el.style.boxShadow = 'none'; el.style.transform = 'translateY(0)'; }}
                      >
                        <div style={{
                          background: thumb.bg,
                          aspectRatio: '16/10',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '20px 24px', position: 'relative',
                        }}>
                          <span style={{
                            fontSize: 20, fontWeight: 800, color: thumb.text,
                            letterSpacing: -0.5, textAlign: 'center',
                          }}>{thumb.slideTitle}</span>
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', opacity: 0.15 }}>
                            <div style={{ width: 70, height: 2, borderRadius: 1, background: thumb.text }} />
                            <div style={{ width: 48, height: 2, borderRadius: 1, background: thumb.text }} />
                          </div>
                        </div>
                        <div style={{ padding: '10px 16px 14px' }}>
                          <div style={{
                            fontSize: 'var(--fs-sm)', fontWeight: 600,
                            color: 'var(--text-primary)', marginBottom: 2,
                          }}>{demo.title}</div>
                          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                            {demo.narrative}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {hasMoreTemplates && (
                  <div ref={templateSentinelRef} style={{ height: 1 }} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel — preview */}
        {selectedDeck && <div className="v8ma-right-panel" role="region" aria-label="Presentation preview" style={{
          flex: 1, height: 'var(--app-height)', position: 'relative',
          borderLeft: '1px solid var(--border)', overflow: 'hidden',
        }}>
          {renderRightPanelContent()}
          <button className="editorial-preview-close" aria-label="Close preview" onClick={() => {setSelectedDeckId(null); requestAnimationFrame(()=>document.querySelector<HTMLButtonElement>(`[data-deck-id="${selectedDeckId}"]`)?.focus());}}><X size={18}/></button>

          {/* Floating Enter button at bottom center */}
          {selectedDeckId && (
            <div style={{
              position: 'absolute', bottom: 36, left: 0, right: 0,
              display: 'flex', justifyContent: 'center', zIndex: 10,
              pointerEvents: 'none',
              animation: 'maFadeIn 0.3s ease both',
            }}>
              <button
                onClick={() => { enteringRef.current = true; setEntering(true); onSelectDeck(selectedDeckId); }}
                style={{
                  pointerEvents: 'auto',
                  fontFamily: 'inherit', fontSize: 'calc(22px * var(--fs-scale))', fontWeight: 600,
                  color: 'var(--text-primary)',
                  background: 'var(--bg-glass)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: 'none',
                  borderRadius: 24,
                  padding: '10px 28px',
                  cursor: 'pointer', letterSpacing: '-0.01em',
                  transition: 'color 0.2s, transform 0.2s ease, background 0.2s',
                  transformOrigin: 'center',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                Open &rarr;
              </button>
            </div>
          )}
        </div>}
      </div>

      {libraryOpen && workspaceId && (
        <WorkspaceLibrary
          workspaceId={workspaceId}
          onOpenDeck={(id) => { setLibraryOpen(false); onSelectDeck(id); }}
          onOpenArtifact={setLibraryViewer}
          onClose={() => setLibraryOpen(false)}
        />
      )}
      {libraryViewer && (
        <ArtifactViewer target={libraryViewer} onClose={() => setLibraryViewer(null)} />
      )}
      {menuOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 5 }} onClick={() => { setMenuOpen(null); setMoveSubmenu(null); }} />}

      {/* Delete folder confirmation overlay */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setDeleteConfirm(null)} style={{ position: 'absolute', inset: 0, background: 'var(--bg-glass)' }} />
          <div style={{ position: 'relative', width: 360, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, padding: 32, textAlign: 'center', zIndex: 1 }}>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 400, color: 'var(--text-primary)', marginBottom: 12 }}>
              Delete &ldquo;{deleteConfirm.folder.name}&rdquo;?
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              This will delete the folder and move {deleteConfirm.folder.deck_count} presentation{deleteConfirm.folder.deck_count !== 1 ? 's' : ''} to unfiled.
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 8 }}>
              Type folder name to confirm:
            </div>
            <input
              autoFocus
              value={deleteConfirm.inputValue}
              onChange={e => setDeleteConfirm({ ...deleteConfirm, inputValue: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)', border: '1px solid var(--border)', borderRadius: 4, outline: 'none', fontFamily: 'inherit', textAlign: 'center', marginBottom: 20, boxSizing: 'border-box' }}
            />
            <div
              onClick={() => {
                if (deleteConfirm.inputValue === deleteConfirm.folder.name) {
                  deleteFolder(deleteConfirm.folder.id, deleteConfirm.inputValue).then(() => {
                    setFolders(prev => prev.filter(f => f.id !== deleteConfirm.folder.id));
                    setDeleteConfirm(null);
                  });
                }
              }}
              style={{
                fontSize: 'var(--fs-md)', fontWeight: 600,
                color: deleteConfirm.inputValue === deleteConfirm.folder.name ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: deleteConfirm.inputValue === deleteConfirm.folder.name ? 'pointer' : 'default',
                transition: 'color 0.2s',
              }}
            >
              Delete
            </div>
          </div>
        </div>
      )}

      {/* Delete deck confirmation overlay */}
      {deleteDeckConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setDeleteDeckConfirm(null)} style={{ position: 'absolute', inset: 0, background: 'var(--bg-glass)' }} />
          <div style={{ position: 'relative', width: 360, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, padding: 32, textAlign: 'center', zIndex: 1 }}>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 400, color: 'var(--text-primary)', marginBottom: 12 }}>
              Delete &ldquo;{deleteDeckConfirm.deck.title}&rdquo;?
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              This presentation and all its content will be deleted.
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 8 }}>
              Type the name to confirm:
            </div>
            <input
              autoFocus
              value={deleteDeckConfirm.inputValue}
              onChange={e => setDeleteDeckConfirm({ ...deleteDeckConfirm, inputValue: e.target.value })}
              onKeyDown={e => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter' && deleteDeckConfirm.inputValue === deleteDeckConfirm.deck.title) {
                  onDeleteDeck(deleteDeckConfirm.deck.id);
                  setSelectedDeckId(null);
                  setDeleteDeckConfirm(null);
                }
                if (e.key === 'Escape') setDeleteDeckConfirm(null);
              }}
              style={{ width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)', border: '1px solid var(--border)', borderRadius: 4, outline: 'none', fontFamily: 'inherit', textAlign: 'center', marginBottom: 20, boxSizing: 'border-box' }}
            />
            <div
              onClick={() => {
                if (deleteDeckConfirm.inputValue === deleteDeckConfirm.deck.title) {
                  onDeleteDeck(deleteDeckConfirm.deck.id);
                  setSelectedDeckId(null);
                  setDeleteDeckConfirm(null);
                }
              }}
              style={{
                fontSize: 'var(--fs-md)', fontWeight: 600,
                color: deleteDeckConfirm.inputValue === deleteDeckConfirm.deck.title ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: deleteDeckConfirm.inputValue === deleteDeckConfirm.deck.title ? 'pointer' : 'default',
                transition: 'color 0.2s',
              }}
            >
              Delete
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
